// DoctorDegreeForm.tsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  PagerTargetEvent,
  HeaderThElement,
} from "@progress/kendo-react-data-tools";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconButton from "../../components/common/IconButton";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import AlertBox from "../../components/common/AlertBox";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import { MdDelete, MdEdit } from "react-icons/md";
import {
  deleteDoctorDegreeInfo,
  getDoctorDegreeByDoctorID,
  insertDoctorDegreeInfo,
  updateDoctorDegreeInfo,
} from "./services/doctor.service";
import { clearDoctorDegreeInfos } from "./doctorSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { getAllActiveDegree } from "../degree/services/degree.services";
import FormYearPicker from "../../components/formFields/FormYearField";
import moment from "moment";

interface PageState {
  skip: number;
  take: number;
}
const initialDataState: PageState = { skip: 0, take: 10 };

const DoctorDegreeForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const DoctorID = location.state?.DoctorID;

  const [editItem, setEditItem] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formKey, setFormKey] = useState(1);
  const [page, setPage] = useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  const DegreeList = useAppSelector((state) => state.degree.DegreeList);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const degreeInfos = useAppSelector((state) => state.doctor.DoctorDegreeInfos);
  const loading = useAppSelector((state) => state.doctor.loading);
  const formLoading = useAppSelector((state) => state.doctor.formLoading);

  useEffect(() => {
    setFormKey((p) => p + 1);
  }, [degreeInfos, editItem]);

  useEffect(() => {
    dispatch(getAllActiveDegree());
    if (DoctorID) dispatch(getDoctorDegreeByDoctorID(DoctorID));
    return () => {
      dispatch(clearDoctorDegreeInfos());
    };
  }, [DoctorID]);

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? degreeInfos.length : event.page.take;
    if (targetEvent.value) setPageSizeValue(targetEvent.value);
    setPage({ ...event.page, take });
  };

  const handleAdd = async (values: any) => {
    const response = await dispatch(
      insertDoctorDegreeInfo({
        DoctorID,
        DegreeID: values.DegreeID || 0,
        CollegeName: values.CollegeName,
        Year: values.Year ? Number(moment(values.Year).format("YYYY")) : 0,
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorDegreeByDoctorID(DoctorID));
      setEditItem(null);
    }
  };

  const handleUpdate = async (values: any) => {
    const response = await dispatch(
      updateDoctorDegreeInfo({
        ID: editItem.ID,
        DoctorID,
        DegreeID: values.DegreeID || 0,
        CollegeName: values.CollegeName,
        Year: values.Year ? Number(moment(values.Year).format("YYYY")) : 0,
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorDegreeByDoctorID(DoctorID));
      setEditItem(null);
    }
  };

  const handleDelete = async () => {
    if (deleteId) {
      const response = await dispatch(deleteDoctorDegreeInfo(deleteId));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getDoctorDegreeByDoctorID(DoctorID));
        dispatch(closeDialog());
        setDeleteId(null);
      }
    }
  };

  const ActionCell = (props: any) => (
    <td
      className={props.className}
      style={{
        ...props.style,
        textAlign: "center",
        display: "flex",
        justifyContent: "space-evenly",
      }}
    >
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Edit"
        onClick={() =>
          setEditItem({
            ...props.dataItem,
            Year: props.dataItem?.Year
              ? moment(props.dataItem?.Year, "YYYY").toDate()
              : "",
          })
        }
        icon={MdEdit}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "20px" }}
      />
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Delete"
        className="color-red"
        onClick={() => {
          dispatch(openDialog("deleteDoctorDegree"));
          setDeleteId(props.dataItem.ID);
        }}
        icon={MdDelete}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "24px" }}
      />
    </td>
  );

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 3 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <td {...(props as any).tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  return (
    <>
      {dialogName === "deleteDoctorDegree" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Degree</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this degree?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={handleDelete}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}

      <GridLayout style={{ marginTop: 20 }} gap={{ rows: 10, cols: 10 }}>
        <GridLayoutItem colSpan={4}>
          <ShadowCard style={{ padding: 20 }}>
            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              {editItem ? "Edit Degree" : "Add Degree"}
            </Typography.h4>
            <Form
              key={formKey}
              onSubmit={editItem ? handleUpdate : handleAdd}
              initialValues={editItem || { CollegeName: "", Year: "" }}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <GridLayout
                    style={{ marginRight: 40 }}
                    gap={{ rows: 15, cols: 20 }}
                    cols={[
                      { width: "33%" },
                      { width: "33%" },
                      { width: "34%" },
                    ]}
                  >
                    <GridLayoutItem>
                      <Field
                        name="DegreeID"
                        label="Select Degree"
                        placeholder="Degree"
                        isAddNew={true}
                        drawerName="degreeForm"
                        component={FormSelectionField}
                        options={DegreeList?.map((item) => ({
                          value: item.ID,
                          label: item.Title,
                        }))}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="CollegeName"
                        label="College / University"
                        placeholder="College name"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      {/* <Field
                        name="Year"
                        label="Passing Year"
                        placeholder="e.g., 2015"
                        component={FormTextField}
                        type="number"
                        validator={requiredValidator}
                        astrike={true}
                      /> */}
                      <Field
                        name="Year"
                        label="Year"
                        format="yyyy"
                        component={FormYearPicker}
                        maxDate={moment().toDate()}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      colSpan={3}
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        gap: "10px",
                      }}
                    >
                      {editItem && (
                        <Button
                          type="button"
                          fillMode={"outline"}
                          themeColor={"secondary"}
                          onClick={() => setEditItem(null)}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <ButtonWithLoading
                        label={editItem ? "Update" : "Add Degree"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || formLoading}
                        loading={formLoading}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />

            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              Degrees ({degreeInfos?.length || 0})
            </Typography.h4>
            {degreeInfos && degreeInfos.length > 0 ? (
              <KendoGrid
                style={{ maxHeight: "calc(100vh - 400px)" }}
                data={degreeInfos.slice(page.skip, page.take + page.skip)}
                skip={page.skip}
                take={page.take}
                total={degreeInfos.length}
                pageable={{
                  buttonCount: 5,
                  pageSizes: [5, 10, "All"],
                  pageSizeValue,
                  type: "input",
                }}
                onPageChange={pageChange}
                cells={{ headerCell: HeaderCustomCell, data: MyDataCustomCell }}
              >
                <Column field="DegreeName" title="Degree" />
                <Column field="CollegeName" title="College / University" />
                <Column field="Year" title="Year" width={120} />
                <Column
                  field="ID"
                  title="Actions"
                  width={110}
                  cells={{ data: ActionCell }}
                  filterable={false}
                  locked={true}
                />
              </KendoGrid>
            ) : (
              <AlertBox />
            )}
          </ShadowCard>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default DoctorDegreeForm;
